import React, { useEffect, useState, useCallback } from "react";
import { MessageCircle } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import CommentDrawer from "./CommentDrawer";

const lastSeenKey = (et, eid) => `comments:lastseen:${et}:${eid}`;

/**
 * Floating / inline button that opens a CommentDrawer and shows an unread
 * badge based on the last time the current user opened the thread.
 *
 * variant="floating" — fixed bottom-right pill (default for project pages)
 * variant="inline"   — small round icon button (use inside lists/rows)
 */
export default function DiscussButton({
  entityType,
  entityId,
  projectId,
  title,
  variant = "inline",
  label = "Discuss",
}) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(async () => {
    if (!entityType || !entityId) { setUnread(0); return; }
    try {
      const list = await v2Api.comments.list(entityType, entityId);
      const lastSeen = Number(localStorage.getItem(lastSeenKey(entityType, entityId)) || 0);
      const n = (list || []).filter((c) => {
        const t = c.created_at ? Date.parse(c.created_at) : 0;
        return t > lastSeen;
      }).length;
      setUnread(n);
    } catch { /* non-fatal */ }
  }, [entityType, entityId]);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  const handleOpen = () => {
    if (entityId) {
      localStorage.setItem(lastSeenKey(entityType, entityId), String(Date.now()));
      setUnread(0);
    }
    setOpen(true);
  };

  const handleClose = (v) => {
    setOpen(v);
    if (!v) refreshUnread();  // poll after close to surface anything posted while open
  };

  if (!entityId) return null;

  if (variant === "floating") {
    return (
      <>
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-30 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg p-4 flex items-center gap-2 transition-transform hover:scale-105"
          title="Discuss"
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          <span className="text-sm font-medium hidden md:inline">{label}</span>
        </button>
        <CommentDrawer
          entityType={entityType}
          entityId={entityId}
          projectId={projectId}
          title={title}
          open={open}
          onOpenChange={handleClose}
        />
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="relative text-slate-300 hover:text-blue-500"
        title="Discuss"
      >
        <MessageCircle className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-3.5 h-3.5 px-0.5 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <CommentDrawer
        entityType={entityType}
        entityId={entityId}
        projectId={projectId}
        title={title}
        open={open}
        onOpenChange={handleClose}
      />
    </>
  );
}
